import React, { useEffect, useState } from 'react';
import Rodal from '../../../components/Rodal/Rodal';
import { ModalBody, ModalHeader } from 'reactstrap';
import './BuyerSelectionPopup.scss';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../../slices/marketplace/timeline/models/engagement-data.model';
import { EngagementProcess } from '../../../slices/marketplace/timeline/models/engagement-process.model';
import BuyerProfilePreviewPopup from '../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import {
    BuyerSelectionOptionsList,
    TimelineActionType,
} from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { IdNdaSelectionForm } from './IdNdaSelectionForm/IdNdaSelectionForm';
import { FinancialModelSelectionForm } from './FinancialModelSelectionForm/FinancialModelSelectionForm';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import { useDispatch } from 'react-redux';
import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import BuyerSelectionPaymentComponent, {
    SelectionPaymentDetails,
} from '../../../components/PaymentDialog/BuyerSelectionPaymentComponent/BuyerSelectionPaymentComponent';
import {
    BuyerSelectionType,
    BuyerSelectionTypeOptions,
} from '../../../wallet/models/BuyerSelectionType';
import { LoadMarketplacePricing } from '../../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import { areAllAvailableBuyersSelected } from './areAllAvailableBuyersSelected';
import { Timeline } from '../../../slices/marketplace/timeline/models/timeline.model';
import { sellersOwnRoundOf } from '../../../slices/marketplace/timeline/utils/catch-up-wait';

interface BuyerSelectionPopupProps {
    isAdminView: boolean;
    isReadOnly?: boolean;
    isVisible: boolean;
    setIsVisible(value: boolean): void;
    fetchData(): void;
    activeEventId: string;
    events: TimelineEvent[];
    engagementData: EngagementData;
    engagementProcess: EngagementProcess;
    engagementId: number;
    fteDvms: number;
    levelOfService: LevelOfService;
    sellersOwnTimeline?: Timeline;
}

const BuyerSelectionPopup = (props: BuyerSelectionPopupProps) => {
    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);
    const [selectedBuyerId, setSelectedBuyerId] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;
    const [
        isFinalizeSelectionPopupVisible,
        setIsFinalizeSelectionPopupVisible,
    ] = useState(false);
    const [typeOfSelectionForConfirmation, setTypeOfSelectionForConfirmation] =
        useState('');
    const [isPaymentPopupVisible, setIsPaymentPopupVisible] = useState(false);
    const [selectionPaymentDetails, setSelectionPaymentDetails] =
        useState<SelectionPaymentDetails>(null);
    useEffect(() => {
        dispatch(LoadMarketplacePricing(props.engagementId));
    }, []);

    const finalizeSelection = async () => {
        try {
            const isIdNda = getActionType() === BuyerSelectionType.IdNda;
            const url = isIdNda
                ? `/api/Marketplace/${props.engagementId}/MarketplaceEventActions/FinalizeIdNdaSelection/${props.activeEventId}`
                : `/api/Marketplace/Engagements/${props.engagementId}/CompleteEvent/${props.activeEventId}`;
            const response = isIdNda
                ? await axios.post(url)
                : await axios.put(url);

            if (response && response.status === 200) {
                createNotification(
                    'Selection successfully finalized.',
                    'success'
                );
            } else {
                const serverMessage = (response as any)?.response?.data?.title;
                createNotification(
                    serverMessage ||
                        'An error occurred while finalizing Selection.',
                    'error'
                );
            }
        } catch (error: any) {
            const serverMessage = error?.response?.data?.title;
            createNotification(
                serverMessage ||
                    'An error occurred while finalizing Selection.',
                'error'
            );
        } finally {
            props.fetchData();
            setIsFinalizeSelectionPopupVisible(false);
            props.setIsVisible(false);
        }
    };

    const onFinalizeSelectionClick = () => {
        const targetEvent = props.events.find(
            (event) => event.id === props.activeEventId
        );
        if (
            areAllAvailableBuyersSelected(
                props.engagementProcess?.buyers,
                targetEvent?.linkedAction?.id
            )
        ) {
            finalizeSelection();
        } else {
            setIsFinalizeSelectionPopupVisible(true);
        }
    };

    const getActionType = () => {
        const targetEvent = props.events.find(
            (event) => event.id === props.activeEventId
        );
        if (!targetEvent) {
            return null;
        }
        const action = targetEvent.linkedAction;
        if (
            !action ||
            !BuyerSelectionOptionsList.some((option) => {
                return option.value === action.actionType;
            })
        ) {
            return null;
        }
        switch (action.actionType) {
            case TimelineActionType.SellerBuyerSelectionIdNda:
                return BuyerSelectionType.IdNda;
            case TimelineActionType.SellerBuyerSelectionDetailedFinancialModel:
                return BuyerSelectionType.FinancialModel;
            default:
                return null;
        }
    };

    const renderSelectionForm = () => {
        const targetEvent = props.events.find(
            (event) => event.id === props.activeEventId
        );
        if (!targetEvent) {
            return <></>;
        }
        const action = targetEvent.linkedAction;
        if (
            !action ||
            !BuyerSelectionOptionsList.some((option) => {
                return option.value === action.actionType;
            })
        ) {
            return <></>;
        }
        if (
            action.actionType === TimelineActionType.SellerBuyerSelectionIdNda
        ) {
            return (
                <IdNdaSelectionForm
                    isAdminView={props.isAdminView}
                    isReadOnly={props.isReadOnly}
                    eventId={targetEvent.id}
                    targetEvent={targetEvent}
                    actionId={action.id}
                    sellersOwnRound={sellersOwnRoundOf(targetEvent, props.sellersOwnTimeline)}
                    engagementData={props.engagementData}
                    engagementProcess={props.engagementProcess}
                    engagementId={props.engagementId}
                    setSelectedBuyerId={setSelectedBuyerId}
                    isLoading={isLoading}
                    onPayAndSendClick={handlePayAndSendClick}
                    onCancelClick={() => {
                        props.setIsVisible(false);
                        props.fetchData();
                    }}
                    onFinalizeSelectionClick={onFinalizeSelectionClick}
                    setTypeOfSelectionForConfirmation={() => {
                        setTypeOfSelectionForConfirmation('ID NDAs');
                    }}
                    setIsBuyerProfilePopupVisible={
                        setIsBuyerProfilePopupVisible
                    }
                    levelOfService={props.levelOfService}
                />
            );
        }

        if (
            action.actionType ===
            TimelineActionType.SellerBuyerSelectionDetailedFinancialModel
        ) {
            return (
                <FinancialModelSelectionForm
                    isAdminView={props.isAdminView}
                    isReadOnly={props.isReadOnly}
                    events={props.events}
                    targetEvent={targetEvent}
                    actionId={action.id}
                    sellersOwnRound={sellersOwnRoundOf(targetEvent, props.sellersOwnTimeline)}
                    engagementData={props.engagementData}
                    engagementProcess={props.engagementProcess}
                    engagementId={props.engagementId}
                    isLoading={isLoading}
                    onPayAndSendClick={handlePayAndSendClick}
                    setSelectedBuyerId={setSelectedBuyerId}
                    onCancelClick={() => {
                        props.setIsVisible(false);
                        props.fetchData();
                    }}
                    onFinalizeSelectionClick={onFinalizeSelectionClick}
                    setTypeOfSelectionForConfirmation={() => {
                        setTypeOfSelectionForConfirmation(
                            'detailed financial models'
                        );
                    }}
                    setIsBuyerProfilePopupVisible={
                        setIsBuyerProfilePopupVisible
                    }
                    eventId={targetEvent.id}
                    levelOfService={props.levelOfService}
                />
            );
        }
        return <></>;
    };

    const handlePayAndSendClick = (
        selectedBuyerIds: number[],
        pricePerBuyer: number,
        totalPrice: number,
        firstNAsOneCount: number,
        eventId: string,
        creditablePortion: number,
        chargedBuyersCount: number
    ) => {
        setSelectionPaymentDetails({
            selectedBuyerIds,
            pricePerBuyer,
            totalPrice,
            firstNAsOneCount,
            eventId,
            creditablePortion,
            chargedBuyersCount,
        });

        if (!isSfas(props.levelOfService)) {
            setIsPaymentPopupVisible(true);
            return;
        }

        setIsLoading(true);
        axios
            .post(
                `/api/Marketplace/${props.engagementId}/MarketplaceEventActions/Select`,
                { buyerIds: selectedBuyerIds, eventId: props.activeEventId }
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during buyer selection.',
                        'error'
                    );
                } else handleScussessfulPayment();
            })
            .catch(() => {
                createNotification(
                    'An Error occured during buyer selection.',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleScussessfulPayment = () => {
        props.fetchData();
        props.setIsVisible(false);
        setIsPaymentPopupVisible(false);
    };

    return (
        <>
            <Rodal
                visible={props.isVisible}
                onClose={() => props.setIsVisible(false)}
                animation="slideUp"
                className="data-nda-terms-popup"
                showMask={true}
                width={750}
                showCloseButton={true}>
                <ModalHeader>
                    <b>
                        Buyer Selection -{' '}
                        {BuyerSelectionTypeOptions.find(
                            (option) => option.value === getActionType()
                        )?.label +
                            (!isSfas(props.levelOfService)
                                ? ' Fee'
                                : '')}
                    </b>
                </ModalHeader>
                <ModalBody>
                    <div className="buyer-selection-popup body_white_box">
                        {renderSelectionForm()}
                    </div>
                </ModalBody>
            </Rodal>
            {isBuyerProfilePopupVisible ? (
                <BuyerProfilePreviewPopup
                    buyerId={selectedBuyerId}
                    close={() => setIsBuyerProfilePopupVisible(false)}
                />
            ) : (
                <></>
            )}
            {isFinalizeSelectionPopupVisible ? (
                <ConfirmationModal
                    isVisible={isFinalizeSelectionPopupVisible}
                    setIsVisible={setIsFinalizeSelectionPopupVisible}
                    header={'Finalize selection'}
                    confirmationText={`By finalizing selection you will no longer be able to offer ${typeOfSelectionForConfirmation} to Buyers.`}
                    onConfirm={finalizeSelection}
                    nextButtonText={'Finalize selection'}
                    cancelButtonText={'Cancel'}
                    params={undefined}
                />
            ) : (
                <></>
            )}
            {isPaymentPopupVisible ? (
                <BuyerSelectionPaymentComponent
                    isVisible={isPaymentPopupVisible}
                    actionType={getActionType()}
                    setIsVisible={setIsPaymentPopupVisible}
                    creditablePortion={
                        selectionPaymentDetails.creditablePortion
                    }
                    paymentSuccess={() => handleScussessfulPayment()}
                    engagementId={props.engagementId}
                    selectedBuyers={selectionPaymentDetails.selectedBuyerIds}
                    chargedBuyersCount={selectionPaymentDetails.chargedBuyersCount}
                    pricePerBuyer={selectionPaymentDetails.pricePerBuyer}
                    totalPrice={selectionPaymentDetails.totalPrice}
                    firstNAsOneCount={selectionPaymentDetails.firstNAsOneCount}
                    eventId={selectionPaymentDetails.eventId}
                />
            ) : (
                <></>
            )}
        </>
    );
};

export default BuyerSelectionPopup;
